import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Send, CheckCircle2 } from "lucide-react";
import { company } from "../mock/mock";

const initialState = {
  nom: "",
  prenom: "",
  email: "",
  telephone: "",
  adresseDepart: "",
  adresseArrivee: "",
  date: "",
  formule: "",
  message: "",
};

const serviceOptions = [
  "Économique +",
  "Économique",
  "Standard",
  "Premium",
  "Garde-meubles",
  "Location de matériel",
  "Transport d'objet",
  "Transport objets lourds",
  "Transport objets d'art",
  "Déménagement professionnel",
  "Autre / je ne sais pas encore",
];

const Field = ({ label, name, type = "text", value, onChange, required, placeholder, textarea }) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={name} className="text-sm font-medium text-gray-700">
      {label} {required && <span className="text-[#C9A227]">*</span>}
    </label>
    {textarea ? (
      <textarea
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        rows={4}
        placeholder={placeholder}
        className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A] focus:border-transparent transition"
      />
    ) : (
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A] focus:border-transparent transition"
      />
    )}
  </div>
);

const QuoteForm = ({ compact }) => {
  const [form, setForm] = useState(initialState);
  const [submitted, setSubmitted] = useState(false);
  const location = useLocation();

  // Pre-select the formule / service passed via URL (e.g. /contact?formule=Standard)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const preselect = params.get("formule") || params.get("service");
    if (preselect) {
      setForm((prev) => ({ ...prev, formule: preselect }));
    }
  }, [location.search]);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mocked submission: stored in localStorage. Backend will replace this.
    const existing = JSON.parse(localStorage.getItem("dmp_quotes") || "[]");
    existing.push({ ...form, createdAt: new Date().toISOString() });
    localStorage.setItem("dmp_quotes", JSON.stringify(existing));
    setSubmitted(true);
    setForm(initialState);
    setTimeout(() => setSubmitted(false), 6000);
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl p-10 shadow-xl border border-gray-100 text-center animate-fadeUp">
        <div className="mx-auto w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-4">
          <CheckCircle2 size={36} className="text-green-600" />
        </div>
        <h3 className="text-2xl font-bold text-[#1B2A4A] mb-2">Demande envoyée !</h3>
        <p className="text-gray-600">
          Merci pour votre demande. Notre équipe vous contactera dans les plus brefs délais
          pour établir votre devis personnalisé.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-2xl p-6 sm:p-8 shadow-xl border border-gray-100"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Nom" name="nom" value={form.nom} onChange={handleChange} required placeholder="Votre nom" />
        <Field label="Prénom" name="prenom" value={form.prenom} onChange={handleChange} required placeholder="Votre prénom" />
        <Field label="Email" name="email" type="email" value={form.email} onChange={handleChange} required placeholder="vous@email.com" />
        <Field label="Téléphone" name="telephone" type="tel" value={form.telephone} onChange={handleChange} required placeholder="06 12 34 56 78" />
        <Field label="Adresse de départ" name="adresseDepart" value={form.adresseDepart} onChange={handleChange} required placeholder="Ville de départ" />
        <Field label="Adresse d'arrivée" name="adresseArrivee" value={form.adresseArrivee} onChange={handleChange} required placeholder="Ville d'arrivée" />
        <Field label="Date préférée" name="date" type="date" value={form.date} onChange={handleChange} />
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-gray-700">Type de bien</label>
          <select
            name="typeBien"
            onChange={handleChange}
            className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A] focus:border-transparent transition bg-white"
          >
            <option value="">Sélectionnez...</option>
            <option>Studio</option>
            <option>Appartement</option>
            <option>Maison</option>
            <option>Bureau / Local pro</option>
          </select>
        </div>
        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <label htmlFor="formule" className="text-sm font-medium text-gray-700">
            Formule / Service souhaité
          </label>
          <select
            id="formule"
            name="formule"
            value={form.formule}
            onChange={handleChange}
            className={`rounded-lg border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A] focus:border-transparent transition bg-white ${
              form.formule ? "border-[#C9A227] ring-1 ring-[#C9A227]/30" : "border-gray-300"
            }`}
          >
            <option value="">Sélectionnez une formule ou un service...</option>
            {serviceOptions.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
            {form.formule && !serviceOptions.includes(form.formule) && (
              <option value={form.formule}>{form.formule}</option>
            )}
          </select>
          {form.formule && (
            <span className="text-xs text-[#C9A227] font-medium">
              ✓ Pré-sélectionné : {form.formule}
            </span>
          )}
        </div>
      </div>
      <div className="mt-4">
        <Field label="Message" name="message" value={form.message} onChange={handleChange} textarea placeholder="Décrivez votre projet de déménagement..." />
      </div>
      <button
        type="submit"
        className="mt-6 w-full flex items-center justify-center gap-2 bg-[#1B2A4A] hover:bg-[#26406b] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors duration-200 shadow-sm"
      >
        <Send size={18} /> Envoyer la demande
      </button>
      <p className="text-xs text-gray-400 text-center mt-3">
        Devis gratuit et sans engagement • Réponse rapide à {company.email}
      </p>
    </form>
  );
};

export default QuoteForm;
